import { useMemo, useState } from 'react'
import {
  GoogleMap,
  InfoWindowF,
  MarkerF,
  MarkerClustererF,
} from '@react-google-maps/api'
import { useMapsReady } from '../../hooks/useMapsReady'
import MapUnavailable from '../MapUnavailable'

const MAP_CENTER = { lat: 28.4595, lng: 77.0266 }
const MAP_CONTAINER_STYLE = { width: '100%', height: '520px' }

export type MapOrder = {
  id: string
  order_number: string
  customer_name?: string | null
  status: string
  lat: number
  lng: number
  address_line?: string | null
  agent_name?: string | null
}

const STATUS_COLORS: Record<string, string> = {
  placed: '#2563EB',
  agent_assigned: '#D6B97B',
  out_for_delivery: '#16A34A',
}

const DEFAULT_FILTERS = ['placed', 'agent_assigned', 'out_for_delivery']

type OrdersMapViewProps = {
  orders: MapOrder[]
  onAssignAgent: (orderId: string) => void
  onViewOrder: (orderId: string) => void
}

const buildIcon = (color: string) => ({
  path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z',
  fillColor: color,
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 1,
  scale: 1.2,
})

export default function OrdersMapView({ orders, onAssignAgent, onViewOrder }: OrdersMapViewProps) {
  const [selected, setSelected] = useState<MapOrder | null>(null)
  const [filters, setFilters] = useState<string[]>(DEFAULT_FILTERS)

  const { keyMissing, isLoaded, loadError } = useMapsReady()

  const filteredOrders = useMemo(() => {
    if (filters.length === 0) {
      return orders
    }
    return orders.filter((order) => filters.includes(order.status))
  }, [filters, orders])

  const validOrders = useMemo(
    () => filteredOrders.filter((order) => order.lat && order.lng && order.lat !== 0 && order.lng !== 0),
    [filteredOrders]
  )
  const missingGeoCount = filteredOrders.length - validOrders.length

  if (keyMissing) return <MapUnavailable reason="keyMissing" height={520} />
  if (loadError) return <MapUnavailable reason="loadError" height={520} />
  if (!isLoaded) {
    return (
      <div className="font-body" style={{ padding: '20px', color: '#9CAB9A' }}>
        Loading map...
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '220px minmax(0, 1fr)' }}>
      <aside
        style={{
          border: '1px solid #EAE4D8',
          borderRadius: '16px',
          padding: '12px',
          background: '#FFFFFF',
          height: 'fit-content',
        }}
      >
        <p className="font-body" style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 700, color: '#9CAB9A', textTransform: 'uppercase' }}>
          Filter Status
        </p>
        {DEFAULT_FILTERS.map((status) => (
          <label key={status} className="font-body" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', fontSize: '13px', color: '#0F2E2A' }}>
            <input
              type="checkbox"
              checked={filters.includes(status)}
              onChange={(event) => {
                setFilters((prev) =>
                  event.target.checked
                    ? [...prev, status]
                    : prev.filter((item) => item !== status),
                )
              }}
            />
            <span style={{ textTransform: 'capitalize' }}>{status.replace('_', ' ')}</span>
          </label>
        ))}
      </aside>

      <div style={{ border: '1px solid #EAE4D8', borderRadius: '16px', overflow: 'hidden' }}>
        {missingGeoCount > 0 && (
          <div className="font-body" style={{
            padding: '12px 16px', margin: '0 0 12px',
            background: 'var(--color-warning-bg)',
            border: '1px solid var(--color-warning-border)',
            borderRadius: '10px',
            color: 'var(--color-warning)',
            fontSize: '13px',
          }}>
            ⚠ {missingGeoCount} order{missingGeoCount > 1 ? 's' : ''} not shown
            Customer addresses are missing GPS coordinates.
          </div>
        )}
        <GoogleMap mapContainerStyle={MAP_CONTAINER_STYLE} zoom={11} center={MAP_CENTER}>
          <MarkerClustererF>
            {(clusterer) => (
              <>
                {validOrders.map((order) => (
                  <MarkerF
                    key={order.id}
                    position={{ lat: order.lat, lng: order.lng }}
                    clusterer={clusterer}
                    icon={buildIcon(STATUS_COLORS[order.status] || '#2563EB')}
                    onClick={() => setSelected(order)}
                  />
                ))}
              </>
            )}
          </MarkerClustererF>

          {selected ? (
            <InfoWindowF
              position={{ lat: selected.lat, lng: selected.lng }}
              onCloseClick={() => setSelected(null)}
            >
              <div style={{ maxWidth: '220px' }}>
                <div className="font-body" style={{ fontSize: '13px', fontWeight: 700, color: '#0F2E2A' }}>
                  {selected.order_number}
                </div>
                <div className="font-body" style={{ fontSize: '12px', color: '#4A5568', marginBottom: '6px' }}>
                  {selected.customer_name || 'Customer'}
                </div>
                <div className="font-body" style={{ fontSize: '11px', color: '#9CAB9A', marginBottom: '8px' }}>
                  {selected.address_line || 'Address unavailable'}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    type="button"
                    className="font-body"
                    onClick={() => onAssignAgent(selected.id)}
                    style={{
                      border: '1px solid #D6B97B',
                      background: '#FFF9E7',
                      color: '#0F2E2A',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    Assign Agent
                  </button>
                  <button
                    type="button"
                    className="font-body"
                    onClick={() => onViewOrder(selected.id)}
                    style={{
                      border: '1px solid #EAE4D8',
                      background: '#FFFFFF',
                      color: '#0F2E2A',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    View Order
                  </button>
                </div>
              </div>
            </InfoWindowF>
          ) : null}
        </GoogleMap>
      </div>
    </div>
  )
}
